import { Leaf } from '@understory-io/leaf/provider';
import { IntlProvider } from 'react-intl';
import { I18nProvider } from 'react-aria-components';
import { data, Outlet } from 'react-router';
import {
  storefrontContext,
  validateLocaleSpecified,
} from '~/domain/core/index.server';
import { PluginsProvider } from '~/domain/plugins/provider';
import { RootLoaderData, useRootData } from '~/root';
import type { Route } from './+types/layout';
import { handleMissingTranslations } from '~/lib/localization/missing-translations-handler';
import { Page } from '~/ui/app-shell/layouts';
import { NavigationProgress } from '~/ui/navigation-progress';
import { ReactRouterLinkComponent } from '~/lib/react-router';

export async function loader({ context, params }: Route.LoaderArgs) {
  const storefront = context.get(storefrontContext);

  const isValidLocale = validateLocaleSpecified(storefront, params.locale);
  if (!isValidLocale) {
    throw data(null, { status: 404 });
  }

  return {};
}

export default function LocaleLayout() {
  const storefront = useRootData();

  return (
    <IntlAware {...storefront}>
      {/* Inside Leaf, which carries the tenant's accent colour. */}
      <NavigationProgress />
      <Page>
        <Outlet />
      </Page>
    </IntlAware>
  );
}

function IntlAware({
  companyId,
  storefrontId,
  companyName,
  fqdn,
  locale,
  nonce,
  preview,
  themeOverrides,
  marketingProviders,
  translationMessages,
  colorMode,
  children,
}: RootLoaderData & { children: React.ReactNode }) {
  return (
    // `Leaf` mounts an `IntlProvider` of its own that merges its `leaf.*`
    // catalogue over whatever it inherits, so it has to sit INSIDE ours — the
    // innermost provider is the only one a component below it reads. Nested the
    // other way round, our catalogue (which carries no `leaf.*` ids) shadows
    // Leaf's and every Leaf string silently falls back to English.
    <IntlProvider
      locale={locale}
      messages={translationMessages}
      onError={handleMissingTranslations}
    >
      <Leaf
        locale={locale}
        theme="storefront"
        colorMode={colorMode ?? undefined}
        themeOverrides={themeOverrides}
        linkComponent={ReactRouterLinkComponent}
      >
        {/* Localises react-aria components (e.g. the availability calendar's
            month/day names, date formatting) to the storefront locale. */}
        <I18nProvider locale={locale}>
          <PluginsProvider
            companyId={companyId}
            storefrontId={storefrontId}
            companyName={companyName}
            fqdn={fqdn}
            locale={locale}
            nonce={nonce}
            preview={preview}
            marketingProviders={marketingProviders}
          >
            {children}
          </PluginsProvider>
        </I18nProvider>
      </Leaf>
    </IntlProvider>
  );
}
